import React from 'react';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { PersonAddAlt1Outlined as InviteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ContextBalances, FinancialContext, PairBalance } from '../../../services/contextApi';
import { formatMoney } from '../../../theme/chart';
import { personColor, tokens } from '../../../theme/tokens';
import { computeBudget } from '../../../utils/budget';
import { BalancesCard } from '../BalancesCard';
import { MemberSpendingCard } from '../MemberSpendingCard';
import { CategoryBudgetBars } from '../home/CategoryBudgetBars';
import { GroupRecent, GroupMember } from '../home/RecentTransactions';
import { BigNumber, Eyebrow, Meter, Panel, PersonDot, Tile } from '../home/ui';
import { useGroupHome, GroupMonth } from './useGroupHome';

export interface GroupHomeProps {
  group: FinancialContext;
  /** the month in focus, YYYY-MM */
  month: string;
  balances: ContextBalances | null;
  currentUserId?: string;
  onSettle: (pair: PairBalance) => void;
  onInvite: () => void;
  onSeeTransactions: () => void;
  /** an entry to mark in the latest ones: the one a link from Fin came to show */
  highlightId?: string;
  /** the month picker, beside the invite button */
  monthPicker?: React.ReactNode;
}

const row = (min: number) =>
  ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`, gap: { xs: 2, md: 2.5 }, alignItems: 'stretch' }) as const;

/**
 * The month in the group: the total, the user's part of it and what they
 * paid. `wide` lays it out in two columns, for when it has the row to itself.
 */
const MonthCard: React.FC<{ group: FinancialContext; month: string; data: GroupMonth; currentUserId?: string; wide?: boolean }> = ({
  group,
  month,
  data,
  currentUserId,
  wide,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const money = (value: number) => formatMoney(value, group.defaultCurrency, i18n.language);
  const monthName = new Date(`${month}-01T12:00:00`).toLocaleDateString(i18n.language, { month: 'long' });
  const label = t('group.month.label', { month: monthName });
  const part = currentUserId ? data.parts.get(currentUserId) ?? 0 : 0;
  const paid = currentUserId ? data.paid.get(currentUserId) ?? 0 : 0;
  const counted = group.settings?.includeInPersonalFinances !== false;
  const budget = Number(group.settings?.monthlyBudget) || 0;
  const breakdown = computeBudget({ spent: data.total, committed: 0, limit: budget });

  let note: string;
  if (!counted) note = t('group.month.notCounted');
  else if (Math.round(paid) > Math.round(part)) note = t('group.month.countedPaidMore', { part: money(part), paid: money(paid) });
  else note = t('group.month.counted', { part: money(part) });

  const total = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
      <Eyebrow>{label}</Eyebrow>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <BigNumber size={wide ? 44 : 34}>{money(data.total)}</BigNumber>
        <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>{t('group.month.entries', { count: data.entries })}</Box>
      </Box>
      {breakdown.hasLimit && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Meter ratio={data.total / budget} color={breakdown.overBy > 0 ? tokens.warn : tokens.accent} track={tokens.line} />
          <Box component='span' sx={{ fontSize: 13, color: breakdown.overBy > 0 ? tokens.warn : tokens.ink2 }}>
            {breakdown.overBy > 0
              ? t('group.month.overBudget', { amount: money(breakdown.overBy), budget: money(budget) })
              : t('group.month.leftOfBudget', { amount: money(breakdown.remaining), budget: money(budget) })}
          </Box>
        </Box>
      )}
    </Box>
  );
  const shares = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
        <Tile label={t('group.month.yourPart')} value={money(part)} size={wide ? 24 : 21} />
        <Tile label={t('group.month.youPaid')} value={money(paid)} size={wide ? 24 : 21} />
      </Box>
      <Box component='p' sx={{ m: 0, fontSize: 13, color: tokens.ink2, lineHeight: 1.5 }}>{note}</Box>
    </Box>
  );

  if (wide) {
    // The panel is the container; the grid inside it splits once the panel is wide enough.
    return (
      <Panel label={label} hero sx={{ containerType: 'inline-size' }}>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            alignItems: 'center',
            '@container (min-width: 620px)': { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr)', columnGap: 5 },
          }}
        >
          {total}
          {shares}
        </Box>
      </Panel>
    );
  }

  return (
    <Panel label={label} hero sx={{ gap: 1.75 }}>
      {total}
      {shares}
    </Panel>
  );
};

const membersOf = (balances: ContextBalances | null): GroupMember[] =>
  balances?.members.map((member) => ({ userId: member.userId, firstName: member.firstName })) ?? [];

/**
 * The top of a group's Início: its name and who is in it, with `actions` on
 * the other side (the month picker and "Convidar" on the dashboard; nothing
 * while the group is being set up).
 */
export const GroupHeader: React.FC<{
  group: FinancialContext;
  balances: ContextBalances | null;
  currentUserId?: string;
  actions?: React.ReactNode;
}> = ({ group, balances, currentUserId, actions }) => {
  const { t, i18n } = useTranslation(['dashboard', 'contexts']);
  const members = membersOf(balances);
  const others = members.filter((member) => member.userId !== currentUserId).map((member) => member.firstName);
  const names = [t('dashboard:memberSpending.you'), ...others];
  const peopleLine = names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} ${t('dashboard:group.and')} ${names[names.length - 1]}`;
  const typeLabel = t(`contexts:types.${group.type}`, { defaultValue: '' });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.75 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
        <Box component='h1' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em', overflowWrap: 'anywhere' }}>
          {group.name}
        </Box>
        {members.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ display: 'flex' }} aria-hidden>
              {members.slice(0, 5).map((member, index) => {
                const person = personColor(index);
                return (
                  <Box key={member.userId} sx={{ ml: index ? '-8px' : 0 }}>
                    <PersonDot name={member.firstName} color={person.color} tint={person.tint} size={30} ring />
                  </Box>
                );
              })}
            </Box>
            <Box component='span' sx={{ fontSize: 14, color: tokens.ink2 }}>
              {peopleLine}
              {typeLabel && ` · ${typeLabel.toLocaleLowerCase(i18n.language)}`}
            </Box>
          </Box>
        )}
      </Box>
      {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>{actions}</Box>}
    </Box>
  );
};

/**
 * The group's Início: who is in it, who owes whom (with the action that
 * settles it), the month in the group, who paid, where the money went and
 * the latest entries. No income or leftover: a group spends together, it
 * doesn't earn.
 */
export const GroupHome: React.FC<GroupHomeProps> = ({ group, month, balances, currentUserId, onSettle, onInvite, onSeeTransactions, highlightId, monthPicker }) => {
  const { t } = useTranslation(['dashboard', 'contexts']);
  const { data, isLoading, error } = useGroupHome(group.id, month);

  const members = membersOf(balances);
  const indexOf = (userId: string) => Math.max(members.findIndex((member) => member.userId === userId), 0);

  const settled = Boolean(balances && balances.pairs.length === 0);

  const spending = members.map((member) => ({
    userId: member.userId,
    firstName: member.firstName,
    paid: data?.paid.get(member.userId) ?? 0,
    part: data?.parts.get(member.userId) ?? 0,
    index: indexOf(member.userId),
  }));

  return (
    <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
      <GroupHeader
        group={group}
        balances={balances}
        currentUserId={currentUserId}
        actions={
          <>
            {monthPicker}
            <Button variant='outlined' startIcon={<InviteIcon />} onClick={onInvite} sx={{ minHeight: 44 }}>
              {t('dashboard:group.invite')}
            </Button>
          </>
        }
      />

      {error && <Alert severity='error'>{t('dashboard:errors.failedToLoad')}</Alert>}

      {settled && balances ? (
        // All settled: the balances shrink to one line and the month gets the whole row, as on a phone.
        <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
          <BalancesCard balances={balances} currentUserId={currentUserId} onSettle={onSettle} />
          {data ? (
            <MonthCard group={group} month={month} data={data} currentUserId={currentUserId} wide />
          ) : (
            <Skeleton variant='rounded' height={200} sx={{ borderRadius: '22px' }} />
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 2.5 } }}>
          <Box sx={{ flex: '3 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', '& > *': { flex: 1 } }}>
            {balances ? (
              <BalancesCard balances={balances} currentUserId={currentUserId} onSettle={onSettle} />
            ) : (
              <Skeleton variant='rounded' height={240} sx={{ borderRadius: '22px' }} />
            )}
          </Box>
          <Box sx={{ flex: '2 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', '& > *': { flex: 1 } }}>
            {data ? (
              <MonthCard group={group} month={month} data={data} currentUserId={currentUserId} />
            ) : (
              <Skeleton variant='rounded' height={240} sx={{ borderRadius: '22px' }} />
            )}
          </Box>
        </Box>
      )}

      {data ? (
        <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 }, opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
          <Box sx={row(420)}>
            <MemberSpendingCard members={spending} currency={group.defaultCurrency} currentUserId={currentUserId} />
            <CategoryBudgetBars categories={data.categories} currency={group.defaultCurrency} />
          </Box>
          <GroupRecent transactions={data.recent} members={members} currentUserId={currentUserId} onSeeAll={onSeeTransactions} highlightId={highlightId} />
        </Box>
      ) : (
        isLoading && <Skeleton variant='rounded' height={300} sx={{ borderRadius: '20px' }} />
      )}
    </Box>
  );
};
